import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Matter } from "@/shared/contexts/DashboardDataContext";

interface MatterPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matters: Matter[];
  conflictingMatters?: string[]; // Matter IDs that are in conflict
  onSelect: (matterId: string) => void;
  onCancel: () => void;
  title?: string;
  description?: string;
}

export const MatterPickerDialog: React.FC<MatterPickerDialogProps> = ({
  open,
  onOpenChange,
  matters,
  conflictingMatters = [],
  onSelect,
  onCancel,
  title = "Select a Matter",
  description = "Choose which matter to assign to the combined entry:",
}) => {
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter and rank matters based on search query
  const filteredMatters = (() => {
    if (!searchQuery.trim()) {
      // No search query - show recent matters first (they have is_recent=1)
      return matters.slice(0, 20);
    }

    const query = searchQuery.toLowerCase();

    // Score each matter based on match quality
    const scored = matters
      .map((matter) => {
        let score = 0;
        const matterName = matter.matter.toLowerCase();
        const client = matter.client.toLowerCase();
        const description = matter.description?.toLowerCase() || "";

        // Exact match gets highest score
        if (matterName === query) score += 100;
        else if (matterName.startsWith(query)) score += 50;
        else if (matterName.includes(query)) score += 25;

        if (client === query) score += 80;
        else if (client.startsWith(query)) score += 40;
        else if (client.includes(query)) score += 20;

        if (description.includes(query)) score += 10;

        // Boost recent matters slightly
        if (matter.is_recent) score += 5;

        return { matter, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 20)
      .map((item) => item.matter);

    return scored;
  })();

  // If conflicting matters provided, show those first
  const conflictMatters = conflictingMatters.length > 0
    ? matters.filter(m => conflictingMatters.includes(m.matter_id))
    : [];

  const handleSelect = () => {
    if (selectedMatterId) {
      onSelect(selectedMatterId);
      setSelectedMatterId(null);
      setSearchQuery("");
    }
  };

  const handleCancel = () => {
    setSelectedMatterId(null);
    setSearchQuery("");
    onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* Search Input */}
        <div className="py-2">
          <input
            type="text"
            placeholder="Search matters by name, client, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Conflict Warning */}
        {conflictMatters.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-2">
            <p className="text-sm text-yellow-800 font-medium mb-2">
              ⚠️ The selected entries have different matters:
            </p>
            <ul className="text-sm text-yellow-700 space-y-1">
              {conflictMatters.map((matter) => (
                <li key={matter.matter_id}>
                  • {matter.matter} - {matter.client}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Matter List */}
        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-md">
          {filteredMatters.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No matters found matching "{searchQuery}"
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredMatters.map((matter) => (
                <div
                  key={matter.matter_id}
                  onClick={() => setSelectedMatterId(matter.matter_id)}
                  className={`p-3 cursor-pointer transition-colors hover:bg-gray-50 ${
                    selectedMatterId === matter.matter_id
                      ? "bg-blue-50 border-l-4 border-blue-500"
                      : ""
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {matter.matter}
                      </div>
                      <div className="text-sm text-gray-600">
                        Client: {matter.client}
                      </div>
                      {matter.description && (
                        <div className="text-sm text-gray-500 mt-1">
                          {matter.description}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        Matter ID: {matter.matter_id}
                      </div>
                    </div>
                    {selectedMatterId === matter.matter_id && (
                      <div className="ml-2 text-blue-500 font-bold">✓</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSelect}
            disabled={!selectedMatterId}
            className={selectedMatterId ? "" : "opacity-50 cursor-not-allowed"}
          >
            Select Matter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
